import type { IntegrationStatus } from "@/config/integrations";

import { create } from "zustand";

interface IntegrationStatusState {
  /** Cached statuses keyed by service ID (e.g. "google_drive") */
  statuses: Record<string, IntegrationStatus>;
  /** Timestamp of last bulk fetch, for TTL-based cache invalidation */
  lastFetchedAt: number | null;

  setStatus: (serviceId: string, status: IntegrationStatus) => void;
  setStatuses: (statuses: Record<string, IntegrationStatus>) => void;
  getStatus: (serviceId: string) => IntegrationStatus | undefined;
  isCacheStale: (ttlMs?: number) => boolean;
  clearStatuses: () => void;
}

const DEFAULT_TTL_MS = 60_000; // 1 minute

export const useIntegrationStatusStore = create<IntegrationStatusState>((set, get) => ({
  statuses: {},
  lastFetchedAt: null,

  setStatus: (serviceId, status) =>
    set((state) => ({
      statuses: { ...state.statuses, [serviceId]: status },
    })),

  setStatuses: (statuses) => set({ statuses, lastFetchedAt: Date.now() }),

  getStatus: (serviceId) => get().statuses[serviceId],

  isCacheStale: (ttlMs = DEFAULT_TTL_MS) => {
    const { lastFetchedAt } = get();

    if (!lastFetchedAt) return true;

    return Date.now() - lastFetchedAt > ttlMs;
  },

  clearStatuses: () => set({ statuses: {}, lastFetchedAt: null }),
}));
